import { useEffect, useState } from "react";
import { Star, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { Reveal } from "@/components/site/Reveal";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", tag: "", text: "" });
  const [sending, setSending] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (form.name.trim().length < 2 || form.text.trim().length < 5) {
      toast.error("Escreva o seu nome e um testemunho.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/reviews", form);
      toast.success(data.message || "Testemunho enviado!");
      setForm({ name: "", tag: "", text: "" });
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSending(false);
  };

  const hasReviews = reviews.length > 0;

  return (
    <section id="testemunhos" className="py-24 lg:py-36 bg-rose-800 text-white relative overflow-hidden grain">
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{ backgroundImage: `url(${IMAGES.heroTexture})`, backgroundSize: "cover", mixBlendMode: "soft-light" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-rose-200">Testemunhos</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-2xl leading-[1.02]">
              Histórias de quem voltou a sentir-se em casa
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="open-review-dialog"
                  className="btn-rose inline-flex items-center gap-2 rounded-full bg-white text-rose-800 px-6 py-3 text-sm">
                  <Plus className="w-4 h-4" /> Deixar testemunho
                </button>
              </DialogTrigger>
              <DialogContent className="bg-white text-stone-900" data-testid="review-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Partilhe a sua experiência</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label htmlFor="r-name">O seu nome</Label>
                    <Input id="r-name" data-testid="review-name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2"
                      placeholder="Como quer aparecer (ex.: Maria S.)" />
                  </div>
                  <div>
                    <Label htmlFor="r-tag">Tipo de consulta (opcional)</Label>
                    <Input id="r-tag" data-testid="review-tag" value={form.tag}
                      onChange={(e) => setForm({ ...form, tag: e.target.value })} className="mt-2"
                      placeholder="ex.: Consulta Individual" />
                  </div>
                  <div>
                    <Label htmlFor="r-text">Testemunho</Label>
                    <Textarea id="r-text" data-testid="review-text" rows={4} value={form.text}
                      onChange={(e) => setForm({ ...form, text: e.target.value })} className="mt-2"
                      placeholder="Escreva algumas palavras sobre a sua experiência." />
                  </div>
                  <p className="text-xs text-stone-400">O seu testemunho será publicado após aprovação da Inês.</p>
                </div>
                <DialogFooter>
                  <button onClick={submit} disabled={sending} data-testid="submit-review"
                    className="btn-rose inline-flex items-center justify-center gap-2 rounded-full bg-rose-800 text-white px-6 py-3 text-sm disabled:opacity-50 w-full">
                    {sending && <Loader2 className="w-4 h-4 animate-spin" />} Enviar testemunho
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Reveal>
        </div>

        {hasReviews ? (
          <div className="mt-16 grid md:grid-cols-3 gap-6" data-testid="reviews-list">
            {reviews.slice(0, 9).map((t) => (
              <Reveal key={t.id} delay={0.05}>
                <figure className="h-full flex flex-col justify-between rounded-3xl bg-white/10 backdrop-blur-sm border border-white/15 p-8">
                  <div>
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-rose-200 text-rose-200" />)}
                    </div>
                    <blockquote className="font-display text-2xl leading-snug italic text-rose-50">
                      “{t.text}”
                    </blockquote>
                  </div>
                  <figcaption className="mt-8 pt-6 border-t border-white/15">
                    <p className="text-white">{t.name}</p>
                    {t.tag && <p className="text-xs text-rose-200 mt-1">{t.tag}</p>}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal delay={0.1}>
            <p className="mt-14 text-rose-100 max-w-xl">
              Seja a primeira pessoa a partilhar a sua experiência. Os testemunhos ajudam quem ainda
              hesita em dar o primeiro passo. 🌸
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
};
